import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

// layouts
import { MainLayout } from 'layouts';

// components
import { Loading } from 'components/common';
import Text from 'components/ui/Text';
import { formatDoorControlHistory } from 'components/all/_lib/formatDoorControlHistory';

// utils
import { api } from 'utils';

// 출입문 한 대의 원격 조작 기록 (센터 정보 설정 › 출입문 제어 › 기록 보기)
const DoorHistory = () => {
  const location = useLocation();
  const { partner, partnerBranch, seqPartner, seqPartnerBranch, kioskName, spaceName } = location.state;
  const [loading, setLoading] = useState(false);
  const [histories, setHistories] = useState([]);
  const [failed, setFailed] = useState(false);

  const getHistories = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get(`/kiosk/history`, {
        params: { seqPartner, seqPartnerBranch: seqPartnerBranch || 0, kioskName },
      });
      setHistories(data || []);
    } catch (error) {
      console.error('출입문 기록 불러오기 실패:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHistories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout
      withBottomTab={false}
      withHeader={true}
      backgroundColor='white'
      headerProps={{ withBackButton: true, centerTitle: '출입문 기록' }}>
      {loading && <Loading />}
      <Wrapper>
        <CenterBox>
          <Text display='block' color='primary100' fontSize='14px' fontWeight='500' lineHeight='18px'>
            {partner} {partnerBranch}
          </Text>
        </CenterBox>

        <SpaceName>{spaceName}</SpaceName>
        <Notice>관리자앱에서 한 문 열기·상시 문열기만 기록돼요. 최근 30일, 최대 50건까지 보여드려요.</Notice>

        {failed && <EmptyText>기록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</EmptyText>}
        {!failed && !loading && histories.length === 0 && <EmptyText>최근 30일 동안 원격 조작 기록이 없어요.</EmptyText>}

        {histories.length > 0 && (
          <HistoryList>
            {histories.map((history, index) => {
              const { actionLabel, failed: actionFailed, when, who } = formatDoorControlHistory(history);
              return (
                <HistoryItem key={`${history.controlledAt}-${index}`}>
                  <HistoryHead>
                    <ActionLabel>{actionLabel}</ActionLabel>
                    {actionFailed && <FailBadge>실패</FailBadge>}
                  </HistoryHead>
                  <HistoryMeta>
                    {when} · {who}
                  </HistoryMeta>
                </HistoryItem>
              );
            })}
          </HistoryList>
        )}
      </Wrapper>
    </MainLayout>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 0 20px 24px;
`;

const CenterBox = styled.div`
  background: #eef5ff;
  border-radius: 6px;
  margin: 12px 0;
  padding: 12px 0;
  text-align: center;
`;

const SpaceName = styled.p`
  margin: 20px 0 4px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: #202020;
`;

const Notice = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 18px;
  color: ${(props) => props.theme.colors['gray50']};
  word-break: keep-all;
`;

const EmptyText = styled.p`
  margin: 40px 0;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${(props) => props.theme.colors['gray50']};
  word-break: keep-all;
`;

const HistoryList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid #f0f3f9;
`;

const HistoryItem = styled.li`
  padding: 14px 0;
  border-bottom: 1px solid #f0f3f9;
`;

const HistoryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

const ActionLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #202020;
`;

const FailBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  background: #fdecec;
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  color: #da2e2e;
`;

const HistoryMeta = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: ${(props) => props.theme.colors['gray50']};
  word-break: keep-all;
`;

export default DoorHistory;
